/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
'use client';

import 'client-only';
import { clsx } from '@accelint/design-foundation/lib/utils';
import { Radio as AriaRadio } from 'react-aria-components/RadioGroup';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import { useContextProps } from 'react-aria-components/slots';
import { RadioContext } from './context';
import styles from './styles.module.css';
import type { RadioProps } from './types';

/**
 * Radio - Individual radio button option within a RadioGroup
 *
 * Renders a selectable radio button with label. Must be used inside a RadioGroup.
 *
 * @param props - {@link RadioProps}
 * @param props.classNames - Custom CSS class names for radio, control, and label.
 * @param props.children - Label content for the radio button.
 * @returns The rendered Radio component.
 *
 * @example
 * ```tsx
 * <RadioGroup label="Favorite color">
 *   <Radio value="red">Red</Radio>
 *   <Radio value="blue">Blue</Radio>
 * </RadioGroup>
 * ```
 */
export function Radio({ ref, ...props }: RadioProps) {
  [props, ref] = useContextProps(props, ref ?? null, RadioContext);

  const { classNames, children, labelPosition = 'end', ...rest } = props;

  return (
    <AriaRadio
      {...rest}
      ref={ref}
      className={composeRenderProps(classNames?.radio, (className) =>
        clsx(
          'group/radio',
          styles.radio,
          labelPosition === 'start' && styles.labelStart,
          className,
        ),
      )}
    >
      {composeRenderProps(children, (children) => (
        <>
          <span className={clsx(styles.control, classNames?.control)} />
          <span className={clsx(styles.label, classNames?.label)}>
            {children}
          </span>
        </>
      ))}
    </AriaRadio>
  );
}
